import { useCallback, useMemo } from "react";
import { useComparisonQueryStates, isComparisonActive } from "../querySchema";
import type { ComparisonMode, ComparisonOption, ComparisonState, DaySubRange } from "../types";

export const useComparisonState = (): ComparisonState => {
  const [state, setState] = useComparisonQueryStates();

  const isActive = useMemo(() => isComparisonActive(state), [state.cmp_mode, state.cmp_option]);

  const activate = useCallback(
    (params: {
      mode: ComparisonMode;
      option: ComparisonOption;
      count?: number;
      subRange?: DaySubRange;
      customFrom?: string;
      customTo?: string;
    }) => {
      setState({
        cmp_mode: params.mode,
        cmp_option: params.option,
        cmp_count: params.count ?? null,
        cmp_sub: params.subRange ?? null,
        cmp_from: params.customFrom ?? null,
        cmp_to: params.customTo ?? null,
        cmp_dim: state.cmp_dim,
      });
    },
    [setState, state.cmp_dim]
  );

  const setDimension = useCallback(
    (dim: string | null) => {
      setState((prev) => ({ ...prev, cmp_dim: dim }));
    },
    [setState]
  );

  const exit = useCallback(() => {
    setState({
      cmp_mode: null,
      cmp_option: null,
      cmp_count: null,
      cmp_sub: null,
      cmp_from: null,
      cmp_to: null,
      cmp_dim: null,
    });
  }, [setState]);

  return {
    ...state,
    isActive,
    activate,
    setDimension,
    exit,
  };
};
